import { Link } from 'react-router-dom';
import Layout from '../components/Layout';

const pillars = [
  {
    num: '01',
    tag: 'TOOL',
    title: '3 Years Protection',
    body: 'We engineer our tools for demanding industrial environments, and we stand behind them. Registered electric, cordless and pneumatic MontiPower tools used within the Original MontiPower System are protected for 3 years against defects in materials and workmanship. Your tool has an identity — its serial number, production information and digital identification connect it to its warranty and service history.',
  },
  {
    num: '02',
    tag: 'BELT',
    title: 'Original Belts. Engineered Performance.',
    body: 'The belt is not simply a consumable. It is a critical part of the Bristle Blasting system. Bristle spring wire and cloth material, geometry, construction, width and interaction with the accelerator bar determine how energy is transferred to the surface. MontiPower belts carry traceable production and batch identification, and are covered by a 12-month Belt Quality Assurance against defects in materials, manufacture and assembly.',
  },
  {
    num: '03',
    tag: 'PERFORMANCE',
    title: 'The Original System Makes the Difference',
    body: 'Bristle Blasting is not conventional rotary wire brushing. The MontiPower system combines controlled rotational speed, engineered bristle geometry and the accelerator bar to create a repeated impact action. This removes corrosion, scale and coatings while simultaneously generating a clean, textured anchor profile. Tool. Belt. Accelerator bar. Speed. Geometry — each matters. Together, they make the Original MontiPower System.',
  },
  {
    num: '04',
    tag: 'CERTIFIED',
    title: 'The Right Technology. Applied the Right Way.',
    body: 'The MontiPower Certified Applicator Program combines digital learning with practical application training to help operators select, set up and use the correct system for the surface and project. For qualifying projects, MontiPower Application Engineering can support with application guidance, demonstrations, operator training and surface-preparation verification. Certified applicators can access enhanced FIVE project support and warranty services.',
  },
  {
    num: '05',
    tag: 'TRACE',
    title: 'Every Original Can Be Proven.',
    body: 'FIVE brings traceability to surface preparation. MontiPower tools are individually identifiable. Original belt packaging carries production and batch information. Digital identification — QR, BARCODE, RFID or NFC — connects products to registration, service and warranty records. This allows MontiPower to verify components and helps customers protect themselves against incompatible or counterfeit consumables.',
  },
];

export default function FivePage() {
  return (
    <Layout>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <div className="mb-10">
        {/* Eyebrow */}
        <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#e00000' }}>
          MontiPower System Guarantee
        </p>

        {/* FIVE wordmark + tagline */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1
              className="font-black leading-none mb-2"
              style={{ fontSize: 'clamp(72px, 12vw, 120px)', color: '#0b0d0e', letterSpacing: '-0.03em', lineHeight: 1 }}
            >
              FIVE
            </h1>
            <p className="text-lg font-semibold" style={{ color: '#374151' }}>
              Master the Surface. Master the Coating.
            </p>
            <p className="text-sm mt-1" style={{ color: '#8f9b9b' }}>
              3 Years. 5 Assurances. 1 Original System.
            </p>
          </div>
          <Link
            to="/registrations/new"
            style={{ background: '#e00000', color: '#fff', textDecoration: 'none', fontWeight: 600, fontSize: 14, padding: '12px 24px', borderRadius: 8, whiteSpace: 'nowrap', alignSelf: 'flex-start' }}
          >
            Register your product →
          </Link>
        </div>

        {/* Red divider */}
        <div style={{ height: 3, background: '#e00000', borderRadius: 2 }} />
      </div>

      {/* ── Intro text ───────────────────────────────────────────────── */}
      <div className="mb-10 max-w-2xl">
        <p className="text-base leading-relaxed" style={{ color: '#374151' }}>
          A MontiPower tool is more than a machine. It is part of an engineered surface preparation system.
          When you register your tool and use it with Original MontiPower belts, components and the correct
          application method, you qualify for <strong>FIVE</strong> — our 3-year System Guarantee.
        </p>
      </div>

      {/* ── Pillars ──────────────────────────────────────────────────── */}
      <div className="space-y-4 mb-12">
        {pillars.map((p, i) => (
          <div
            key={p.num}
            className="rounded-xl overflow-hidden"
            style={{ background: '#fff', border: '1px solid #e5e5e5', display: 'flex' }}
          >
            {/* Left red number column */}
            <div
              style={{
                width: 64, flexShrink: 0, background: i === 0 ? '#e00000' : '#fafafa',
                borderRight: '1px solid #e5e5e5', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', padding: '20px 0',
              }}
            >
              <span
                style={{
                  fontSize: 22, fontWeight: 900, letterSpacing: '-0.02em',
                  color: i === 0 ? '#fff' : '#e00000', lineHeight: 1,
                }}
              >
                {p.num}
              </span>
            </div>

            {/* Content */}
            <div className="flex-1 px-6 py-5">
              <p
                className="text-xs font-bold uppercase tracking-widest mb-1"
                style={{ color: '#e00000' }}
              >
                {p.tag}
              </p>
              <h3 className="font-bold text-base mb-2" style={{ color: '#0b0d0e' }}>
                {p.title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: '#6b7280' }}>
                {p.body}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Bottom CTA ───────────────────────────────────────────────── */}
      <div
        className="rounded-xl px-8 py-10 sm:px-12"
        style={{ background: '#fff', border: '1px solid #e5e5e5', position: 'relative', overflow: 'hidden' }}
      >
        {/* Decorative red left border */}
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 5, background: '#e00000' }} />

        <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#e00000' }}>
          One System. One Promise.
        </p>
        <h2 className="text-2xl font-black mb-3" style={{ color: '#0b0d0e' }}>
          Don't just buy the tool.<br />Own the system.
        </h2>
        <p className="text-sm mb-6 max-w-lg" style={{ color: '#6b7280', lineHeight: 1.75 }}>
          Use an Original MontiPower tool. Use Original MontiPower belts and components.
          Follow the MontiPower application method. Register your equipment.
          Get <strong>FIVE</strong> protection for up to 3 years.
        </p>

        <div className="flex flex-wrap gap-3">
          <Link
            to="/registrations/new"
            style={{ background: '#e00000', color: '#fff', textDecoration: 'none', fontWeight: 600, fontSize: 14, padding: '11px 22px', borderRadius: 8 }}
          >
            Register now →
          </Link>
          <Link
            to="/claims/new"
            style={{ background: 'transparent', color: '#374151', textDecoration: 'none', fontWeight: 500, fontSize: 14, padding: '11px 22px', borderRadius: 8, border: '1px solid #e5e5e5' }}
          >
            Submit a claim
          </Link>
        </div>
      </div>

    </Layout>
  );
}
